import React, { useState, useContext, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import API from './api';
import { UserContext } from './App';
import { ChatContext } from './ChatContext';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import io from 'socket.io-client';
import {
  FaTimes,
  FaSignOutAlt,
  FaSpinner,
  FaCoins,
  FaBoxes,
  FaChartLine,
  FaTh,
  FaCaretDown,
  FaWallet,
  FaBoxOpen,
  FaCog,
  FaUser,
  FaUserCircle,
  FaSignInAlt,
  FaComments,
  FaPaperPlane,
  FaHeadset,
  FaRobot,
  FaTrophy,
  FaUserShield,
  FaShareAlt,
  FaShieldAlt,
  FaTags,
  FaBullseye,
  FaExchangeAlt,
  FaUsers,
  FaChartBar,
  FaGift
} from 'react-icons/fa';

import styles from './Coinflip.module.css';
import Footer from './Footer';
import './Dashboard.css';
import ChatSidebar from './ChatSidebar';
import { ChatMessageList, ChatInputBar } from './ChatPanel';

const BACKEND_URL = window.location.hostname === 'localhost' ? 'http://localhost:4000' : 'https://test3-0ar4.onrender.com';

const Coinflip = () => {
  const { user, setUser, activeBalance, setActiveBalance, setIsRedeemModalOpen, setIsDepositModalOpen, setIsWithdrawModalOpen } = useContext(UserContext);
  const { chatMessages, sendMessage } = useContext(ChatContext);
  const navigate = useNavigate();

  // PvP state
  const socketRef = useRef(null);
  const [lobbies, setLobbies] = useState([]);
  const [history, setHistory] = useState([]);
  const [activeGame, setActiveGame] = useState(null);
  
  const activeGameRef = useRef(activeGame);
  const userRef = useRef(user);
  const activeBalanceRef = useRef(activeBalance);
  
  useEffect(() => { activeGameRef.current = activeGame; }, [activeGame]);
  useEffect(() => { userRef.current = user; }, [user]);
  useEffect(() => { activeBalanceRef.current = activeBalance; }, [activeBalance]);

  // Game state
  const [bet, setBet] = useState('');
  const [selectedSide, setSelectedSide] = useState('heads');
  const [outcome, setOutcome] = useState(null);
  const [win, setWin] = useState(null);
  const [isFlipping, setIsFlipping] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [doubleDownOffer, setDoubleDownOffer] = useState(null);
  const [doubleDownChoice, setDoubleDownChoice] = useState('red');
  const [doubleDownPending, setDoubleDownPending] = useState(false);
  const [doubleDownResult, setDoubleDownResult] = useState(null);
  const [doubleDownTimeLeft, setDoubleDownTimeLeft] = useState(0);
  const [coinPhase, setCoinPhase] = useState('idle'); // 'idle' | 'spinning' | 'landing'
  const [coinKey, setCoinKey] = useState(0);
  const [resultImage, setResultImage] = useState('/red.png');



  const doubleDownTimeLeftRef = useRef(0);
  const doubleDownOfferRef = useRef(false);
  const activeGameCloseTimer = useRef(null);
  const [recentGames, setRecentGames] = useState(user?.recentGames || []);
  const [balanceFlash, setBalanceFlash] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sortBy, setSortBy] = useState('high');
  const [filterBy, setFilterBy] = useState('all');

  // Layout state
  const [showChat, setShowChat] = useState(() => {
    const saved = localStorage.getItem('showChat');
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [mutedUsers, setMutedUsers] = useState([]);
  const [showBalancePopup, setShowBalancePopup] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showGamesMenu, setShowGamesMenu] = useState(false);
  const [chatInput, setChatInput] = useState('');
  const [sending, setSending] = useState(false);

  // Refs
  const audioRef = useRef(null);
  const popupAudioRef = useRef(null);
  const winAudioRef = useRef(null);
  const loseAudioRef = useRef(null);
  const clickSound = useRef(new Audio('/sounds/click.mp3'));
  const balanceRef = useRef(null);
  const chatEndRef = useRef(null);
  const coinRef = useRef(null);
  const [isJumping, setIsJumping] = useState(false);
  const [countdown, setCountdown] = useState(null);
  
  const sideToColorName = {
    heads: 'Red',
    tails: 'Black',
  };

  const CFXIcon = ({ size = '28px' }) => (
    <img src="/coinimg.png" alt="logo" style={{ width: size, height: size, display: 'inline-block', verticalAlign: 'middle' }} />
  );

  // Effects
  useEffect(() => {
    if (balanceFlash) {
      const timer = setTimeout(() => {
        setBalanceFlash(null);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [balanceFlash]);

  useEffect(() => {
    if (showChat) {
      chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages, showChat]);

  useEffect(() => {
    if (!doubleDownOffer?.expiresAt) {
      setDoubleDownTimeLeft(0);
      return;
    }

    const tick = () => {
      const msLeft = doubleDownOffer.expiresAt - Date.now();
      setDoubleDownTimeLeft(Math.max(0, Math.ceil(msLeft / 1000)));
    };

    tick();
    const timer = setInterval(tick, 250);
    return () => clearInterval(timer);
  }, [doubleDownOffer]);



  // Helper: refresh user balance from API
  const refreshBalance = async () => {
    try {
      const res = await API.get('/auth/me');
      if (res.data) {
        setUser(prev => prev ? ({
          ...prev,
          balance: res.data.balance,
          promoBalance: res.data.promoBalance || prev.promoBalance || 0,
        }) : prev);
      }
    } catch (e) { /* ignore */ }
  };

  // Helper: reset all coin animation state
  const resetCoinState = () => {
    setCoinPhase('idle');
    setCoinKey(k => k + 1);
  };

  useEffect(() => {
    socketRef.current = io(BACKEND_URL, {
      withCredentials: true,
      transports: ['websocket', 'polling'],
    });

    const s = socketRef.current;

    s.on('connect', () => {
      const currUser = userRef.current;
      if (currUser) {
        s.emit('authenticate', { userId: currUser.id || currUser._id });
        s.emit('coinflip_lobbies_get');
      }
    });

    // Listen for balance updates on this socket too
    s.on('balance_update', (data) => {
      if (data && userRef.current) {
        const newBal = data.balance !== undefined ? data.balance : data.newBalance;
        const newPromo = data.promoBalance !== undefined ? data.promoBalance : data.newPromoBalance;
        setUser(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            ...(newBal !== undefined ? { balance: parseFloat(newBal) } : {}),
            ...(newPromo !== undefined ? { promoBalance: parseFloat(newPromo) } : {}),
          };
        });
      }
    });

    s.on('coinflip_lobbies_update', (data) => {
      setLobbies(data || []);
    });

    s.on('coinflip_history_update', (data) => {
      setHistory(data || []);
    });

    s.on('coinflip_created', (game) => {
      setActiveGame(game);
      if (userRef.current) {
        const isPromo = activeBalanceRef.current === 'promo';
        setUser(prev => ({
          ...prev,
          [isPromo ? 'promoBalance' : 'balance']: (isPromo ? prev.promoBalance : prev.balance) - game.amount
        }));
      }
    });

    s.on('coinflip_started', (game) => {
      const currUser = userRef.current;
      const myId = currUser?.id || currUser?._id;
      const ag = activeGameRef.current;
      if (ag && ag.id === game.id) {
        setActiveGame(game);
      } else if (game.joiner && game.joiner.id === myId) {
        setActiveGame(game);
        if (currUser) {
          const isPromo = activeBalanceRef.current === 'promo';
          setUser(prev => ({
            ...prev,
            [isPromo ? 'promoBalance' : 'balance']: (isPromo ? prev.promoBalance : prev.balance) - game.amount
          }));
        }
      } else if (game.creator && game.creator.id === myId) {
        setActiveGame(game);
      }
    });

    s.on('coinflip_result', (data) => {
      const currUser = userRef.current;
      const myId = currUser?.id || currUser?._id;
      const ag = activeGameRef.current;
      if (ag && ag.id === data.gameId) {
        setIsFlipping(true);
        setOutcome(null);
        setWin(null);
        setShowResult(false);
        setActiveGame(prev => prev ? { ...prev, secret: data.secret } : prev);

        const didWin = data.winnerId === myId;
        const isParticipant = ag.creator.id === myId || (ag.joiner && ag.joiner.id === myId);

        // Set result image so landing animation reveals correct face
        setResultImage(data.outcome === 'heads' ? '/red.png' : '/black.png');

        // NEW: Hype Countdown Logic
        const startSequence = async () => {
          setCountdown('3');
          await new Promise(r => setTimeout(r, 400));
          setCountdown('2');
          await new Promise(r => setTimeout(r, 600));
          setCountdown('1');
          await new Promise(r => setTimeout(r, 800));
          setCountdown(null);

          // Phase 2: Start physical flip
          if (coinRef.current) {
            coinRef.current.style.transition = 'none';
            coinRef.current.style.transform = 'rotateY(0deg)';
            coinRef.current.offsetHeight; 
            
            setIsJumping(true);
            const spins = 10 + Math.floor(Math.random() * 4);
            const targetRot = spins * 360 + (data.outcome === 'heads' ? 0 : 180);
            
            coinRef.current.style.transition = 'transform 2.5s cubic-bezier(0.2, 0, 0.3, 1)';
            coinRef.current.style.transform = `rotateY(${targetRot}deg)`;
          }
        };

        startSequence();

        // Phase 3: Show result after full sequence (1.8s countdown + 2.5s flight = 4.3s)
        setTimeout(() => {
          setOutcome(data.outcome);
          
          if (isParticipant) {
            setWin(didWin);
            if (didWin) {
              if (winAudioRef.current) { winAudioRef.current.currentTime = 0; winAudioRef.current.play(); }
              setBalanceFlash('win');
            } else {
              if (loseAudioRef.current) { loseAudioRef.current.currentTime = 0; loseAudioRef.current.play(); }
              setBalanceFlash('lose');
            }
          } else {
            setWin(null);
          }
          
          setShowResult(true);
          setIsFlipping(false);
          refreshBalance();
          
          if (!doubleDownOfferRef.current) {
            activeGameCloseTimer.current = setTimeout(() => {
              setActiveGame(null);
              setShowResult(false);
              setOutcome(null);
              setWin(null);
              resetCoinState();
              s.emit('coinflip_lobbies_get');
            }, 3000);
          }
        }, 4300);
      }
    });

    s.on('coinflip_double_down_offer', (data) => {
      doubleDownOfferRef.current = true;
      if (activeGameCloseTimer.current) {
        clearTimeout(activeGameCloseTimer.current);
        activeGameCloseTimer.current = null;
      }
      // Wait for flip and landing (4.3s) + slight pause before offer UI (Total 5.0s)
      setTimeout(() => {
        setDoubleDownResult(null);
        setDoubleDownChoice('red');
        setDoubleDownOffer({
          gameId: data.gameId,
          payout: data.payout,
          expiresAt: Date.now() + (data.timeoutMs || 15000)
        });
      }, 5000); 
    });

    s.on('coinflip_double_down_resolved', (data) => {
      setDoubleDownPending(false);
      setDoubleDownOffer(null);
      doubleDownOfferRef.current = false;

      if (data.action === 'double_create') {
        setDoubleDownResult(null);
        setShowResult(false);
        setOutcome(null);
        setWin(null);
        resetCoinState();
        return;
      }

      if (data.action === 'double') {
        setIsFlipping(true);
        setShowResult(false);
        setDoubleDownResult(null);

        // Update active game amount to reflect the double down
        setActiveGame(prev => prev ? { ...prev, amount: prev.amount * 2 } : prev);
        // Set result image
        setResultImage(data.rollSide === 'heads' ? '/red.png' : '/black.png');

        // NEW: 3D Controlled Animation (Double Down)
        if (coinRef.current) {
          coinRef.current.style.transition = 'none';
          coinRef.current.style.transform = 'rotateY(0deg)';
          coinRef.current.offsetHeight;

          setIsJumping(true);
          const spins = 8 + Math.floor(Math.random() * 4);
          const targetRot = spins * 360 + (data.rollSide === 'heads' ? 0 : 180);
          
          coinRef.current.style.transition = 'transform 2.5s cubic-bezier(0.15, 0.85, 0.3, 1)';
          coinRef.current.style.transform = `rotateY(${targetRot}deg) translateY(-220px) scale(1.3)`;

          setTimeout(() => {
             if (coinRef.current) coinRef.current.style.transform = `rotateY(${targetRot}deg) translateY(0) scale(1)`;
          }, 1200);
        }

        // Result after 2.5s
        setTimeout(() => {
          setIsJumping(false);
          setDoubleDownResult(data);
          setShowResult(true);
          setIsFlipping(false);

          if (data.win && winAudioRef.current) {
            winAudioRef.current.currentTime = 0; winAudioRef.current.play();
          } else if (!data.win && loseAudioRef.current) {
            loseAudioRef.current.currentTime = 0; loseAudioRef.current.play();
          }

          if (typeof data.balance === 'number') {
            setUser(prev => prev ? ({ ...prev, balance: data.balance }) : prev);
          }
          if (data.payoutAmount > 0) {
            setBalanceFlash('win');
          } else {
            setBalanceFlash('lose');
          }
          s.emit('coinflip_lobbies_get');
          refreshBalance();

          activeGameCloseTimer.current = setTimeout(() => {
            setActiveGame(null);
            setShowResult(false);
            setOutcome(null);
            setWin(null);
            setDoubleDownResult(null);
            resetCoinState();
          }, 3000);
        }, 2500);
      } else {
        // "Take" action, no spin needed
        if (window._addToast) {
          window._addToast({
            type: 'success',
            title: 'PROFIT TAKEN!',
            message: `You successfully cashed out ${(data.payoutAmount || data.payout || 0).toFixed(2)} scrap!`
          });
        }
        
        if (typeof data.balance === 'number') {
          setUser(prev => prev ? ({ ...prev, balance: data.balance }) : prev);
        }
        setBalanceFlash('win');
        s.emit('coinflip_lobbies_get');
        refreshBalance();

        // Close quickly if profit taken
        setActiveGame(null);
        setShowResult(false);
        setDoubleDownResult(null);
        resetCoinState();
      }
    });

    s.on('coinflip_cancelled', (data) => {
      const ag = activeGameRef.current;
      if (ag && ag.id === data.gameId) {
        setActiveGame(null);
      }
      if (userRef.current) {
        setUser(prev => {
          if (!prev) return prev;
          const updated = { ...prev };
          if (data.newBalance !== undefined) updated.balance = data.newBalance;
          if (data.newPromoBalance !== undefined) updated.promoBalance = data.newPromoBalance;
          return updated;
        });
      }
      refreshBalance();
    });

    s.on('coinflip_error', (msg) => {
    if (window._addToast) {
      window._addToast({ type: 'error', title: 'GAME ERROR', message: msg });
    } else {
      alert(msg);
    }
      setLoading(false);
    });

    return () => {
      s.disconnect();
    };
  }, [user?.id, user?._id]);

  // Game functions
  const handleHalfBet = () => {
    if (!user) return;
    if (!bet || parseFloat(bet) === 0) {
      setBet((user.balance / 2).toFixed(2));
    } else {
      const half = Math.max(parseFloat(bet) / 2, 0.01);
      setBet(half.toFixed(2));
    }
  };

  const handleDoubleBet = () => {
    if (!user) return;
    if (!bet || parseFloat(bet) === 0) {
      setBet((1).toFixed(2));
    } else {
      const doubled = Math.min(parseFloat(bet) * 2, user.balance);
      setBet(doubled.toFixed(2));
    }
  };

  const handleAllInBet = () => {
    if (!user) return;
    setBet(user.balance.toFixed(2));
  };

  const handleCreateGame = () => {
    const betAmount = parseFloat(bet);
    if (!user) {
      if (window._addToast) window._addToast({ type: 'error', title: 'AUTH ERROR', message: 'User not loaded' });
      return;
    }
    if (!betAmount || betAmount < 0.1) {
      if (window._addToast) window._addToast({ type: 'error', title: 'BET ERROR', message: 'Minimum bet is 0.10 scrap' });
      return;
    }
    const currentBalance = activeBalance === 'promo' ? (user.promoBalance || 0) : user.balance;
    if (betAmount > currentBalance) {
      if (window._addToast) window._addToast({ type: 'error', title: 'BALANCE ERROR', message: 'Bet exceeds your active balance' });
      return;
    }

    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play();
    }

    setLoading(true);
    socketRef.current.emit('coinflip_create', {
      userId: user.id || user._id,
      username: user.username,
      amount: betAmount,
      side: selectedSide,
      avatar: user.avatar,
      usePromoBalance: activeBalance === 'promo'
    });
    setLoading(false);
  };

  const handleJoinGame = (gameId, amount) => {
    if (!user) return;
    const currentBalance = activeBalance === 'promo' ? (user.promoBalance || 0) : user.balance;
    if (amount > currentBalance) {
      if (window._addToast) window._addToast({ type: 'error', title: 'BALANCE ERROR', message: 'Insufficient balance to join' });
      return;
    }
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play();
    }
    setLoading(true);
    socketRef.current.emit('coinflip_join', {
      userId: user.id || user._id,
      username: user.username,
      gameId,
      avatar: user.avatar,
      usePromoBalance: activeBalance === 'promo'
    });
    setLoading(false);
  };

  const handleViewHistoryGame = (h) => {
    // Robust mapping from history item to active game structure
    const creatorUser = {
      id: h.creatorId || 'history-creator',
      username: typeof h.creator === 'object' ? h.creator.username : h.creator,
      avatar: h.creatorAvatar || (typeof h.creator === 'object' ? h.creator.avatar : null) || `https://api.dicebear.com/7.x/avataaars/svg?seed=${typeof h.creator === 'object' ? h.creator.username : h.creator}&backgroundColor=transparent`,
      side: h.outcome === 'heads' && h.winnerUsername === (typeof h.creator === 'object' ? h.creator.username : h.creator) ? 'heads' : (h.outcome === 'tails' && h.winnerUsername === (typeof h.creator === 'object' ? h.creator.username : h.creator) ? 'tails' : (h.outcome === 'heads' ? 'tails' : 'heads'))
    };

    const joinerUser = h.joiner ? {
      id: h.joinerId || 'history-joiner',
      username: typeof h.joiner === 'object' ? h.joiner.username : h.joiner,
      avatar: h.joinerAvatar || (typeof h.joiner === 'object' ? h.joiner.avatar : null) || `https://api.dicebear.com/7.x/avataaars/svg?seed=${typeof h.joiner === 'object' ? h.joiner.username : h.joiner}&backgroundColor=transparent`,
      side: creatorUser.side === 'heads' ? 'tails' : 'heads'
    } : null;

    setActiveGame({
      id: h.id || Math.floor(Math.random() * 10000000).toString(),
      amount: h.amount,
      status: 'completed',
      creator: creatorUser,
      joiner: joinerUser,
      historyOutcome: h.outcome,
      winner: h.winnerUsername,
      winnerId: h.winnerUsername === creatorUser.username ? creatorUser.id : (joinerUser && h.winnerUsername === joinerUser.username ? joinerUser.id : null),
      outcome: h.outcome,
      seed: h.seed || 'N/A',
      secret: h.secret || 'N/A',
      time: h.time
    });
  };

  const handleCallBot = (gameId) => {
    if (!user) return;
    socketRef.current.emit('coinflip_call_bot', {
      gameId,
      userId: user.id || user._id
    });
  };

  const handleTakeWin = () => {
    if (!doubleDownOffer || !socketRef.current) return;
    setDoubleDownPending(true);
    socketRef.current.emit('coinflip_take_win', { gameId: doubleDownOffer.gameId });
  };

  const handleDoubleDown = (colorParam) => {
    if (!doubleDownOffer || !socketRef.current) return;
    setDoubleDownPending(true);
    socketRef.current.emit('coinflip_double_down', {
      gameId: doubleDownOffer.gameId,
      color: colorParam || doubleDownChoice
    });
  };

  const handleCancelGame = (gameId) => {
    if (!user) return;
    const game = lobbies.find(g => g.id === gameId);
    
    if (game) {
      const gameTime = game.time || Date.now();
      const elapsedMinutes = (Date.now() - gameTime) / 60000;
      if (elapsedMinutes < 5) {
        const remainingMinutes = Math.ceil(5 - elapsedMinutes);
        if (window._addToast) window._addToast({ type: 'warning', title: 'CANCEL ERROR', message: `You must wait ${remainingMinutes} more minute(s) before canceling.` });
        return;
      }
    }
    
    socketRef.current.emit('coinflip_cancel', {
      gameId,
      userId: user.id || user._id
    });
  };

  // Navigation handlers
  const handleTabNavigation = (tab) => {
    clickSound.current.play().catch(() => {});
    navigate(`/dashboard?tab=${tab}`);
  };

  const handleLogoClick = () => {
    clickSound.current.play().catch(() => {});
    navigate('/dashboard');
  };

  // Chat handlers
  const toggleChat = () => {
    clickSound.current.play().catch(() => {});
    const newState = !showChat;
    setShowChat(newState);
    localStorage.setItem('showChat', JSON.stringify(newState));
  };

  const handleSendMessage = () => {
    if (!chatInput.trim()) return;
    setSending(true);
    sendMessage(chatInput.trim());
    setChatInput('');
    setSending(false);
  };

  // Balance handlers
  const toggleBalancePopup = (e) => {
    e.stopPropagation();
    clickSound.current.play().catch(() => {});
    setShowBalancePopup(!showBalancePopup);
  };

  const handleDeposit = () => {
    clickSound.current.play().catch(() => {});
    setShowBalancePopup(false);
    setIsDepositModalOpen(true);
  };

  const handleWithdraw = () => {
    clickSound.current.play().catch(() => {});
    setShowBalancePopup(false);
    setIsWithdrawModalOpen(true);
  };

  const handleWallet = () => {
    clickSound.current.play().catch(() => {});
    setShowBalancePopup(false);
    navigate('/dashboard?tab=wallet');
  };

  const handleLogout = () => {
    clickSound.current.play().catch(() => {});
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    navigate('/dashboard');
  };


  return (
    <div className={styles.cfContainer}>
      <audio ref={audioRef} src="/coinflip.mp3" />
      <audio ref={popupAudioRef} src="/popup-sound.mp3" />
      <audio ref={winAudioRef} src="/win.mp3" />
      <audio ref={loseAudioRef} src="/lose.mp3" />

      <main className={styles.cfMain}>

          {/* ── PAGE HEADER BAR ── */}
          <div className={styles.cfPageHeader}>
          <div className={styles.cfTitleBlock}>
            <div className={styles.cfTitleRow}>
              <CFXIcon size="42px" />
              <span className={styles.cfTitleText}>COINFLIP</span>
            </div>
            <div className={styles.cfSubtitle}>The classic 50/50 game mode.</div>
          </div>

            <div className={styles.cfBetBar}>
              <div className={styles.cfBetLabel}>Bet Amount ~${bet ? parseFloat(bet).toFixed(2) : '0'}</div>
              <div className={styles.cfBetRow}>
                <div className={styles.cfBetInputWrap}>
                  <img src="/scrap.webp" alt="coin" className={styles.cfBetCoinImg} />
                  <input
                    type="number"
                    className={styles.cfBetInput}
                    value={bet}
                    onChange={(e) => setBet(e.target.value)}
                    placeholder="0"
                    disabled={loading}
                    min="0.01"
                    step="0.01"
                  />
                </div>
                <button className={styles.cfQuickChip} onClick={handleHalfBet} disabled={loading}>1/2</button>
                <button className={styles.cfQuickChip} onClick={handleDoubleBet} disabled={loading}>2x</button>
                <button className={styles.cfQuickChip} onClick={handleAllInBet} disabled={loading}>MAX</button>
                <button
                  className={`${styles.cfCoinBtn} ${selectedSide === 'heads' ? styles.cfCoinBtnActive : ''}`}
                  onClick={() => !loading && setSelectedSide('heads')}
                  title="Red (Heads)"
                >
                  <img src="/red.png" alt="Red" className={styles.cfCoinBtnImg} />
                </button>
                <button
                  className={`${styles.cfCoinBtn} ${selectedSide === 'tails' ? styles.cfCoinBtnActive : ''}`}
                  onClick={() => !loading && setSelectedSide('tails')}
                  title="Black (Tails)"
                >
                  <img src="/black.png" alt="Black" className={styles.cfCoinBtnImg} />
                </button>
                <button
                  className={styles.cfCreateBtn}
                  onClick={handleCreateGame}
                  disabled={loading || !bet || parseFloat(bet) <= 0}
                >
                  {loading ? <FaSpinner className="fa-spin" /> : 'Create Game'}
                </button>
              </div>
            </div>
          </div>

          {/* Main Layout Area */}
          {activeGame && createPortal(
            <div className={styles.cfOverlay}>
              <div className={styles.cfActiveGameModal} style={{ position: 'relative' }}>
                
                {/* Double Down Offer Overlay */}
                <AnimatePresence>
                {doubleDownOffer && !doubleDownResult && (
                  <motion.div 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    style={{ position: 'absolute', inset: 0, background: 'rgba(10, 10, 12, 0.98)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '24px', padding: '30px', backdropFilter: 'blur(20px)' }}
                  >
                    <div style={{ width: '100%', maxWidth: '500px' }}>
                      <motion.div
                        initial={{ scale: 0.9, y: 20 }}
                        animate={{ scale: 1, y: 0 }}
                        style={{ textAlign: 'center' }}
                      >
                        <h2 style={{ color: '#ffb703', margin: '0 0 8px 0', fontSize: '38px', fontWeight: 950, letterSpacing: '4px', textTransform: 'uppercase' }}>DOUBLE OR NOTHING?</h2>
                        <p style={{ color: 'rgba(255,255,255,0.6)', margin: '0 0 32px 0', fontSize: '15px', fontWeight: 700 }}>
                          You won <span style={{ color: '#4ade80' }}>{doubleDownOffer.payout.toFixed(2)}</span>! Double it or play it safe?
                        </p>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                          <button 
                            onClick={() => handleDoubleDown('red')} 
                            disabled={doubleDownPending} 
                            style={{ 
                              background: 'rgba(239, 68, 68, 0.1)', 
                              border: '1px solid rgba(239, 68, 68, 0.3)', 
                              padding: '24px', 
                              borderRadius: '20px', 
                              cursor: 'pointer',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(239, 68, 68, 0.2)'; e.currentTarget.style.transform = 'translateY(-4px)'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)'; e.currentTarget.style.transform = 'translateY(0)'; }}
                          >
                            <img src="/red.png" alt="" style={{ width: '50px', marginBottom: '12px' }} />
                            <div style={{ color: 'white', fontWeight: 900, fontSize: '13px' }}>HEADS (RED)</div>
                          </button>

                          <button 
                            onClick={() => handleDoubleDown('black')} 
                            disabled={doubleDownPending} 
                            style={{ 
                              background: 'rgba(255, 255, 255, 0.03)', 
                              border: '1px solid rgba(255, 255, 255, 0.1)', 
                              padding: '24px', 
                              borderRadius: '20px', 
                              cursor: 'pointer',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)'; e.currentTarget.style.transform = 'translateY(-4px)'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255, 255, 255, 0.03)'; e.currentTarget.style.transform = 'translateY(0)'; }}
                          >
                            <img src="/black.png" alt="" style={{ width: '50px', marginBottom: '12px' }} />
                            <div style={{ color: 'white', fontWeight: 900, fontSize: '13px' }}>TAILS (BLACK)</div>
                          </button>
                        </div>

                        <button 
                          onClick={handleTakeWin} 
                          disabled={doubleDownPending} 
                          style={{ 
                            width: '100%',
                            background: '#4ade80', 
                            color: '#052c16', 
                            padding: '20px', 
                            borderRadius: '16px', 
                            fontSize: '15px', 
                            fontWeight: 950, 
                            border: 'none',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '10px',
                            boxShadow: '0 10px 20px rgba(74, 222, 128, 0.2)'
                          }}
                        >
                          <FaExchangeAlt /> TAKE {doubleDownOffer.payout.toFixed(2)}
                        </button>

                        <div style={{ marginTop: '24px', background: 'rgba(255,255,255,0.03)', height: '6px', borderRadius: '10px', overflow: 'hidden', position: 'relative' }}>
                           <motion.div 
                             initial={{ width: '100%' }}
                             animate={{ width: `${(doubleDownTimeLeft / 15) * 100}%` }}
                             style={{ height: '100%', background: '#ffb703', boxShadow: '0 0 10px #ffb703' }}
                           />
                        </div>
                        <div style={{ marginTop: '8px', color: 'rgba(255,255,255,0.2)', fontSize: '11px', fontWeight: 900, letterSpacing: '1px' }}>EXPIRES IN {doubleDownTimeLeft}S</div>
                      </motion.div>
                    </div>
                  </motion.div>
                )}
                </AnimatePresence>

                {/* Double Down Result Overlay (Only for BUSTED/DOUBLED) */}
                <AnimatePresence>
                {doubleDownResult && !doubleDownResult.isTake && (
                  <motion.div 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.95)', zIndex: 2000, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', borderRadius: '16px', padding: '20px', textAlign: 'center', backdropFilter: 'blur(10px)' }}
                  >
                    <motion.div 
                       initial={{ scale: 0.8, rotate: -5 }}
                       animate={{ scale: 1, rotate: 0 }}
                       style={{ background: '#13131a', padding: '40px', borderRadius: '32px', border: `1px solid ${doubleDownResult.win ? '#4ade8022' : '#ef444422'}`, boxShadow: `0 30px 60px rgba(0,0,0,0.5)` }}
                    >
                      <h3 style={{ color: doubleDownResult.win ? '#4ade80' : '#ef4444', margin: '0 0 10px 0', fontSize: '48px', fontWeight: 950, textShadow: doubleDownResult.win ? '0 0 30px rgba(74, 222, 128, 0.4)' : '0 0 30px rgba(239, 68, 68, 0.4)', letterSpacing: '2px' }}>
                        {doubleDownResult.win ? 'DOUBLED!' : 'BUSTED!'}
                      </h3>
                      <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '16px', fontWeight: 700 }}>
                        {doubleDownResult.win ? `You secured +${(doubleDownResult.payoutAmount || 0).toFixed(2)} COINS` : 'Better luck next time.'}
                      </p>
                      <button 
                        onClick={() => { setDoubleDownResult(null); setActiveGame(null); }} 
                        style={{ 
                          marginTop: '32px', 
                          padding: '16px 40px', 
                          background: 'white', 
                          color: 'black', 
                          border: 'none', 
                          borderRadius: '14px', 
                          fontWeight: 900, 
                          cursor: 'pointer',
                          fontSize: '14px',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.05)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
                      >
                        CONTINUE
                      </button>
                    </motion.div>
                  </motion.div>
                )}
                </AnimatePresence>

                {/* Modal Header */}
                <div className={styles.cfModalHeader}>
                  <div className={styles.cfModalTitleRow}>
                    <CFXIcon size="42px" />
                    <span className={styles.cfTitleText}>COINFLIP</span>
                    <span className={styles.cfModalId}>#{activeGame.id || '5122556'}</span>
                  </div>
                  <button className={styles.cfModalClose} onClick={() => setActiveGame(null)}>✕</button>
                </div>

                {/* Modal Content */}
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  gap: '40px',
                  padding: '40px 20px',
                  background: '#111116',
                  minHeight: '440px',
                  width: '100%',
                  maxWidth: '800px',
                  margin: '0 auto',
                  position: 'relative',
                  overflow: 'hidden'
                }}>
                  {/* Left Player */}
                  <div style={{ width: '220px', textAlign: 'center', zIndex: 2 }}>
                    <div style={{ position: 'relative', display: 'inline-block' }}>
                      <img
                        src={activeGame.creator.avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${activeGame.creator.username}`}
                        alt="creator"
                        style={{ width: '160px', height: '160px', borderRadius: '24px', border: outcome === 'heads' ? '5px solid #4ade80' : '2px solid #ffb703', transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)', boxShadow: outcome === 'heads' ? '0 0 30px rgba(74, 222, 128, 0.3)' : '0 10px 25px rgba(0,0,0,0.4)' }}
                      />
                      <div style={{ position: 'absolute', top: '-10px', right: '-10px', width: '42px', height: '42px', borderRadius: '50%', background: activeGame.creator.side === 'heads' ? '#ef4444' : '#111', border: '3px solid #fff', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '12px', fontWeight: 900, boxShadow: '0 4px 10px rgba(0,0,0,0.3)' }}>
                        {activeGame.creator.side === 'heads' ? 'R' : 'B'}
                      </div>
                    </div>
                    <div style={{ color: '#fff', fontWeight: 900, marginTop: '18px', fontSize: '20px', letterSpacing: '0.5px' }}>{activeGame.creator.username}</div>
                  </div>

                  {/* Center Coin + Action */}
                  <div style={{ width: '200px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '25px', zIndex: 2 }}>
                    <div style={{ position: 'relative', width: '150px', height: '150px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      
                      {countdown && (
                        <div style={{ position: 'absolute', top: '75%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 100, fontSize: '56px', fontWeight: '950', color: '#ffb703', textShadow: '0 0 40px rgba(0,0,0,1), 0 0 20px rgba(124, 58, 237, 0.4)', letterSpacing: '3px', pointerEvents: 'none', animation: 'countdown-pop 0.6s ease-out', textAlign: 'center', width: '300px' }}>
                          {countdown}
                        </div>
                      )}
                      
                      {/* Premium Winner Glow */}
                      <div className={[styles.winnerGlow, (showResult && win) ? styles.showWinnerGlow : ''].join(' ')} />

                      <div className={styles.coinNew}>
                         <div ref={coinRef} className={styles.coinInner}>
                            <div className={styles.coinSide} />
                            <div className={`${styles.coinFace} ${styles.frontFace}`}>
                              <img src="/red.png" alt="heads" className={styles.coinImage} />
                            </div>
                            <div className={`${styles.coinFace} ${styles.backFace}`}>
                              <img src="/black.png" alt="tails" className={styles.coinImage} />
                            </div>
                         </div>
                      </div>
                      
                      {/* Shadow with Jump Sync */}
                      <div className={[styles.coinShadow, isJumping ? styles.shadowJumping : ''].join(' ')} />
                    </div>
                    
                    <div style={{ minHeight: '80px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                      {(showResult && outcome) || (activeGame.status === 'completed' && !isFlipping) ? (
                        <div style={{ textAlign: 'center', animation: 'fadeInUp 0.4s ease' }}>
                          <div style={{ color: win ? '#4ade80' : '#ef4444', fontWeight: 950, fontSize: '24px', textTransform: 'uppercase', letterSpacing: '2px', textShadow: win ? '0 0 20px rgba(74, 222, 128, 0.3)' : 'none' }}>
                            {win ? 'YOU WON!' : 'YOU LOST!'}
                          </div>
                          <div style={{ color: '#9ca3af', fontSize: '14px', marginTop: '6px', fontWeight: 700, letterSpacing: '1px' }}>
                            {outcome === 'heads' ? 'RED' : 'BLACK'} LANDED
                          </div>
                        </div>
                      ) : activeGame.status === 'in_progress' || isFlipping ? (
                        <div style={{ color: '#ffb703', fontSize: '18px', fontWeight: 900, letterSpacing: '2px', animation: 'pulseOpacity 0.8s infinite' }}>FLIPPING...</div>
                      ) : activeGame.creator.id === (user?.id || user?._id) ? (
                        <>
                          <div style={{ color: '#ffb703', fontWeight: 900, fontSize: '22px', marginBottom: '12px' }}>VS</div>
                          <button onClick={() => handleCallBot(activeGame.id)} className={styles.cfModalJoinBtn} style={{ padding: '10px 25px', fontSize: '15px' }}>CALL BOT</button>
                        </>
                      ) : (
                        <>
                          <div style={{ color: '#ffb703', fontWeight: 900, fontSize: '22px', marginBottom: '12px' }}>VS</div>
                          <button onClick={() => handleJoinGame(activeGame.id, activeGame.amount)} className={styles.cfModalJoinBtn} style={{ padding: '10px 25px', fontSize: '15px' }}>JOIN GAME</button>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Right Player */}
                  <div style={{ width: '220px', textAlign: 'center', zIndex: 2 }}>
                    {activeGame.joiner ? (
                      <div>
                        <div style={{ position: 'relative', display: 'inline-block' }}>
                          <img 
                            src={activeGame.joiner.avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${activeGame.joiner.username}`} 
                            style={{ width: '160px', height: '160px', borderRadius: '24px', border: outcome === 'tails' ? '5px solid #4ade80' : '2px solid rgba(255,255,255,0.1)', transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)', boxShadow: outcome === 'tails' ? '0 0 30px rgba(74, 222, 128, 0.3)' : '0 10px 25px rgba(0,0,0,0.4)' }} 
                            alt="joiner" 
                          />
                          <div style={{ position: 'absolute', top: '-10px', left: '-10px', width: '42px', height: '42px', borderRadius: '50%', background: activeGame.creator.side === 'heads' ? '#111' : '#ef4444', border: '3px solid #fff', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '12px', fontWeight: 900, boxShadow: '0 4px 10px rgba(0,0,0,0.3)' }}>
                            {activeGame.creator.side === 'heads' ? 'B' : 'R'}
                          </div>
                        </div>
                        <div style={{ color: '#fff', fontWeight: 900, marginTop: '18px', fontSize: '20px', letterSpacing: '0.5px' }}>{activeGame.joiner.username}</div>
                      </div>
                    ) : (
                      <div style={{ width: '160px', height: '160px', background: 'rgba(255,255,255,0.02)', border: '2px dashed rgba(255,255,255,0.06)', borderRadius: '24px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', margin: '0 auto', color: 'rgba(255,255,255,0.2)', fontWeight: 800 }}>
                        <FaSpinner className="fa-spin" style={{ marginBottom: '12px', fontSize: '28px' }} />
                        <span style={{ fontSize: '13px', letterSpacing: '1px' }}>WAITING...</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Modal Footer (Seed info) */}
                <div className={styles.cfModalFooter}>
                  <div className={styles.cfSeedInfo}>
                    <div style={{ paddingBottom: '4px' }}><span className={styles.cfSeedLabel}>HASHED SEED:</span> <span className={styles.cfSeedValue}>{activeGame.seed || 'Waiting...'}</span></div>
                    <div style={{ paddingBottom: '4px' }}><span className={styles.cfSeedLabel}>CLIENT SEED:</span> <span className={styles.cfSeedValue}>{activeGame.joiner ? activeGame.joiner.id : 'Waiting for opponent...'}</span></div>
                    <div><span className={styles.cfSeedLabel}>SECRET:</span> <span className={styles.cfSeedValue} style={activeGame.secret ? {color: '#ffb703', fontWeight: 800} : {}}>{activeGame.secret || 'Waiting...'}</span></div>
                  </div>
                  <div className={styles.cfModalFooterBtns}>
                    <a href={`/fairness?serverSeed=${activeGame.secret || ''}&clientSeed=${activeGame.joiner?.id || ''}`} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                      <button className={styles.cfProvableBtn} title="Verify Roll Fairness" style={{ cursor: 'pointer' }}>
                        <FaUserShield /> <span style={{ fontSize: '12px', marginLeft: '6px' }}>Verify</span>
                      </button>
                    </a>
                    <button className={styles.cfShareBtn} onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}/coinflip?game=${activeGame.id}`);
                      alert('Game link copied to clipboard!');
                    }}>
                      Share <FaShareAlt />
                    </button>
                  </div>
                </div>

              </div>
            </div>,
            document.body
          )}

          {/* ── GAMES LIST ── */}
          <div className={styles.cfGamesPanel}>
              {/* Sub-header bar */}
              <div className={styles.cfGamesBar}>
                <div className={styles.cfGamesBarLeft}>
                  <span className={styles.cfGamesLabel}>ALL GAMES</span>
                  <span className={styles.cfGamesCount}>{lobbies.length + Math.min(history.length, 5)}</span>
                </div>
                <div className={styles.cfGamesBarRight}>
                  <span className={styles.cfSortLabel}>Sort By</span>
                  <select
                    className={styles.cfSortSelect}
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                  >
                    <option value="high">High to Low</option>
                    <option value="low">Low to High</option>
                    <option value="new">Newest First</option>
                  </select>
                  <select
                    className={styles.cfFilterSelect}
                    value={filterBy}
                    onChange={(e) => setFilterBy(e.target.value)}
                  >
                    <option value="all">All Amounts</option>
                    <option value="micro">Under 0.01</option>
                    <option value="small">0.01 – 0.1</option>
                    <option value="medium">0.1 – 1</option>
                    <option value="large">Over 1</option>
                  </select>
                </div>
              </div>

              {/* History ribbon */}
              {history.length > 0 && (
                <div className={styles.cfHistoryRibbon}>
                  <span className={styles.cfHistoryLabel}>Previous:</span>
                  <div className={styles.cfHistoryScroll}>
                    {history.slice(0, 50).map((h, i) => (
                      <img
                        key={i}
                        title={`${h.winnerUsername} won $${(h.amount * 2).toFixed(2)}`}
                        src={h.outcome === 'heads' ? '/red.png' : '/black.png'}
                        alt={h.outcome}
                        className={styles.cfHistoryPip}
                        onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.2)'}
                        onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Game rows */}
              <div className={styles.cfGamesList}>
                {lobbies.length === 0 && history.length === 0 ? (
                  <div className={styles.cfEmptyMsg}>No games found. Create one to play!</div>
                ) : (
                  <>
                    {[...lobbies]
                      .filter(g => {
                        if (filterBy === 'all') return true;
                        if (filterBy === 'micro') return g.amount < 0.01;
                        if (filterBy === 'small') return g.amount >= 0.01 && g.amount < 0.1;
                        if (filterBy === 'medium') return g.amount >= 0.1 && g.amount < 1;
                        if (filterBy === 'large') return g.amount >= 1;
                        return true;
                      })
                      .sort((a, b) => {
                        if (sortBy === 'high') return b.amount - a.amount;
                        if (sortBy === 'low') return a.amount - b.amount;
                        if (sortBy === 'new') return (b.time || 0) - (a.time || 0);
                        return 0;
                      })
                      .map((g) => (
                      <div key={g.id} className={styles.cfGameRow} style={{ opacity: g.status === 'in_progress' ? 0.75 : 1 }}>
                        {/* Creator side */}
                        <div className={styles.cfRowCreator}>
                          <div className={styles.cfRowAvatarWrap}>
                            <img
                              src={g.creator.avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${g.creator.username}&backgroundColor=transparent`}
                              alt="creator"
                              className={styles.cfRowAvatar}
                            />
                          </div>
                          <span className={styles.cfRowName}>{g.creator.username.length > 8 ? g.creator.username.slice(0,8)+'…' : g.creator.username}</span>
                        </div>

                        {/* Center: vs info */}
                        <div className={styles.cfRowCenter}>
                          <FaCoins
                            className={styles.cfRowCoin}
                            style={{ color: '#ffb703', fontSize: '24px', marginRight: '5px' }}
                          />
                          <span className={styles.cfRowVsCount}>{g.joiner ? '2' : '1'}</span>
                          {!g.joiner && <span className={styles.cfRowWaiting}>Waiting...</span>}
                        </div>

                        {/* Joiner side */}
                        <div className={styles.cfRowJoiner}>
                          {g.joiner ? (
                            <>
                              <div className={styles.cfRowAvatarWrap}>
                                <img
                                  src={g.joiner.avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${g.joiner.username}&backgroundColor=transparent`}
                                  alt="joiner"
                                  className={styles.cfRowAvatar}
                                />
                              </div>
                              <span className={styles.cfRowName}>{g.joiner.username.length > 8 ? g.joiner.username.slice(0,8)+'…' : g.joiner.username}</span>
                            </>
                          ) : (
                            <div style={{ width: '44px', height: '44px', borderRadius: '50%', background: 'rgba(255,255,255,0.02)', border: '1px dashed rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.1)', fontSize: '18px', fontWeight: 900 }}>?</div>
                          )}
                        </div>

                        {/* Amount */}
                        <div className={styles.cfRowAmount}>
                          <img src="/scrap.webp" alt="coin" className={styles.cfRowAmountCoin} />
                          {g.amount.toFixed(2)}
                        </div>

                        {/* Actions */}
                        <div className={styles.cfRowActions}>
                          {g.status === 'in_progress' ? (
                            <button onClick={() => setActiveGame(g)} className={styles.cfJoinBtn}>SPECTATE</button>
                          ) : g.creator?.id === (user?.id || user?._id) ? (
                            <>
                              <button onClick={() => handleCancelGame(g.id)} className={`${styles.cfJoinBtn} ${styles.cfCancelBtn}`}>
                                <FaTimes />
                              </button>
                              <button onClick={() => setActiveGame(g)} className={styles.cfEyeBtn} title="View">
                                <span>👁</span>
                              </button>
                            </>
                          ) : (
                            <>
                              <button onClick={() => handleJoinGame(g.id, g.amount)} className={styles.cfJoinBtn}>Join</button>
                              <button onClick={() => setActiveGame(g)} className={styles.cfEyeBtn} title="View">
                                <span>👁</span>
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    ))}

                            {history.slice(0, 5).map((h, i) => (
                      <div key={`hist-${i}`} className={`${styles.cfGameRow} ${styles.cfGameRowDone}`}>
                        <div className={styles.cfRowCreator}>
                          <div className={styles.cfRowAvatarWrap}>
                            <img
                              src={h.creatorAvatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${h.creator}&backgroundColor=transparent`}
                              alt="creator"
                              className={styles.cfRowAvatar}
                            />
                          </div>
                          <span className={styles.cfRowName}>
                            {(() => {
                              const name = (typeof h.creator === 'object' && h.creator) ? h.creator.username : (h.creator || 'Guest');
                              return name.length > 8 ? name.slice(0, 8) + '…' : name;
                            })()}
                          </span>
                        </div>

                        <div className={styles.cfRowCenter}>
                          <img src={h.outcome === 'heads' ? '/red.png' : '/black.png'} alt="result" className={styles.cfRowCoin} />
                          <span className={styles.cfRowVsCount}>2</span>
                          <span className={styles.cfRowWaiting}>{h.outcome === 'heads' ? 'Red' : 'Black'} Won</span>
                        </div>

                        {/* Joiner side for history */}
                        <div className={styles.cfRowJoiner}>
                          {h.joiner ? (
                            <>
                              <div className={styles.cfRowAvatarWrap}>
                                <img
                                  src={h.joinerAvatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${typeof h.joiner === 'object' ? h.joiner.username : h.joiner}&backgroundColor=transparent`}
                                  alt="joiner"
                                  className={styles.cfRowAvatar}
                                />
                              </div>
                              <span className={styles.cfRowName}>
                                {(() => {
                                  const name = typeof h.joiner === 'object' ? h.joiner.username : h.joiner;
                                  return name.length > 8 ? name.slice(0, 8) + '…' : name;
                                })()}
                              </span>
                            </>
                          ) : (
                            <span className={styles.cfRowVsBadge}>PvB MATCH</span>
                          )}
                        </div>
                        <div className={styles.cfRowAmount}>
                          <img src="/scrap.webp" alt="coin" className={styles.cfRowAmountCoin} />
                          {h.amount.toFixed(2)}
                        </div>
                        <div className={styles.cfRowActions}>
                          <button onClick={() => handleViewHistoryGame(h)} className={styles.cfEyeBtn} title="View Match">
                            <span>👁</span>
                          </button>
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </div>
            </div>
        </main>
    </div>
  );
};

export default Coinflip;
